import React from "react";
import {
    Box,
    TextField,
    Button,
    Paper,
    Typography,
    Grid,
    InputAdornment,
    alpha,
    useTheme
} from "@mui/material";
import { DateRange, FilterList } from "@mui/icons-material";

interface ReportFiltersProps {
    filters: { fromDate: string; toDate: string };
    setFilters: React.Dispatch<React.SetStateAction<{ fromDate: string; toDate: string }>>;
    onApply?: () => void;
    onReset?: () => void;
}

const ReportFilters: React.FC<ReportFiltersProps> = ({
    filters,
    setFilters,
    onApply,
    onReset
}) => {
    const theme = useTheme();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const handleApply = () => {
        if (onApply) {
            onApply();
        }
    };

    const handleReset = () => {
        setFilters({ fromDate: "", toDate: "" });
        if (onReset) {
            onReset();
        }
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                mb: 4,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                background: `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.05)}`
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        mr: 2
                    }}
                >
                    <FilterList sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                        Filter Report
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.25 }}>
                        Select date range to filter the report data
                    </Typography>
                </Box>
            </Box>

            <Grid container spacing={2.5} alignItems="flex-end">
                <Grid item xs={12} sm={12} md={4}>
                    <TextField
                        label="From Date"
                        name="fromDate"
                        type="date"
                        size="small"
                        fullWidth
                        value={filters.fromDate || ""}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <DateRange sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                },
                                '&.Mui-focused': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.02),
                                }
                            }
                        }}
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={4}>
                    <TextField
                        label="To Date"
                        name="toDate"
                        type="date"
                        size="small"
                        fullWidth
                        value={filters.toDate || ""}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <DateRange sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                },
                                '&.Mui-focused': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.02),
                                }
                            }
                        }}
                    />
                </Grid>

                 <Grid item xs={12} sm={6} md={2}>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleApply}
                        disabled={!filters.fromDate || !filters.toDate}
                    >
                        Apply Filters
                    </Button>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleReset}
                    >
                        Reset
                    </Button>
                </Grid>
            </Grid>

            {(filters.fromDate || filters.toDate) && (
                <Box
                    sx={{
                        mt: 2.5,
                        pt: 2,
                        borderTop: `1px dashed ${alpha(theme.palette.divider, 0.5)}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        Active filters:
                    </Typography>
                    {filters.fromDate && (
                        <Typography
                            variant="body2"
                            sx={{
                                px: 1.5,
                                py: 0.5,
                                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                borderRadius: 1,
                                color: theme.palette.primary.main,
                                fontWeight: 500
                            }}
                        >
                            From: {new Date(filters.fromDate).toLocaleDateString()}
                        </Typography>
                    )}
                    {filters.toDate && (
                        <Typography
                            variant="body2"
                            sx={{
                                px: 1.5,
                                py: 0.5,
                                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                borderRadius: 1,
                                color: theme.palette.primary.main,
                                fontWeight: 500
                            }}
                        >
                            To: {new Date(filters.toDate).toLocaleDateString()}
                        </Typography>
                    )}
                </Box>
            )}
        </Paper>
    );
};

export default ReportFilters;